import { Link as RouterLink } from "react-router";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import LanguageToggle from "./LanguageToggle";
import LogoSVG from "../img/hero-logo.svg";
import Facebook from "../img/icons/facebook-round.svg";
import Insta from "../img/icons/instagram.svg";
import Linkedin from "../img/icons/linkedin-round.svg";
import Twitter from "../img/icons/twitter-round.svg";
import Youtube from "../img/icons/youtube-round.svg";
import TicTok from "../img/icons/tiktok-round.svg";
import { Wrapper } from "./Layout";

const StyledFooter = styled.div`
  padding-top: ${(props) => props.theme.space[4]};
  padding-bottom: ${(props) => props.theme.space[6]};
  border-top: 5px solid ${({ theme }) => theme.colors.blue};
  background-color: ${({ theme }) => theme.colors.blueDarker4};
  font-family: ${({ theme }) => theme.fonts.impact};

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    font-size: 1.6rem;
  }
`;

const Inner = styled.div`
  margin: 0 auto;
  padding-left: ${(props) => props.theme.space[2]};
  padding-right: ${(props) => props.theme.space[2]};
  text-align: center;
  color: white;
`;

const Logo = styled.img`
  width: 75px;
`;

const LangLogoWrap = styled.div`
  display: flex;
  justify-content: center;
  gap: 60px;
  margin-top: ${({ theme }) => theme.space[3]};

  @media (min-width: ${({ theme }) => theme.breakpoints[0]}) {
    gap: 100px;
  }
`;

const SocialIcon = styled.img`
  width: 20px;
  height: 20px;
  color: white;
  fill: white;
`;

const Link = styled.a`
  color: white;
  line-height: 1;
  margin-right: 1rem;
`;

const InlineBlock = styled.span`
  display: inline-block;
  margin-right: 5px;
`;

const SocialIconWrapper = styled.span`
  display: inline-block;
  font-size: 1rem;
  line-height: 1;
  margin-right: 5px;
`;

const ContactUsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.space[2]};
  flex-wrap: wrap;
  margin: ${({ theme }) => `${theme.space[4]} 0 ${theme.space[6]}`};
`;

const SocialLinksContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.space[4]};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.space[8]};
`;

const SocialLinkWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.space[2]};
`;

const ContentWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${(props) => props.theme.space[2]};
  margin-bottom: ${(props) => props.theme.space[4]};
`;

const Footer = () => {
  const { t } = useTranslation();
  return (
    <StyledFooter>
      <Wrapper>
        <Inner>
          <LangLogoWrap>
            <LanguageToggle />
            <RouterLink to="/">
              <Logo src={LogoSVG} alt="" />
            </RouterLink>
          </LangLogoWrap>
          <ContactUsContainer>
            <div>
              <InlineBlock>{t("footer.email", "Email")}:</InlineBlock>
              <Link href="mailto:support@eliteserien.no">
                support@eliteserien.no
              </Link>
            </div>
            <div>
              <Link href="https://www.eliteserien.no/om-eliteserien">
                {t("footer.contactUs", "Contact Us")}
              </Link>
            </div>
          </ContactUsContainer>
          <SocialLinksContainer>
            <SocialLinkWrap>
              <SocialIconWrapper>
                <SocialIcon src={Facebook} alt="Facebook letter f logo" />
              </SocialIconWrapper>
              <Link
                href="https://www.facebook.com/eliteserien/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Facebook
              </Link>
            </SocialLinkWrap>
            <SocialLinkWrap>
              <SocialIconWrapper>
                <SocialIcon src={Twitter} alt="X logo" />
              </SocialIconWrapper>
              <Link
                href="https://twitter.com/eliteserien"
                target="_blank"
                rel="noopener noreferrer"
              >
                X
              </Link>
            </SocialLinkWrap>
            <SocialLinkWrap>
              <SocialIconWrapper>
                <SocialIcon src={Insta} alt="Instagrams instant camera logo" />
              </SocialIconWrapper>
              <Link
                href="https://www.instagram.com/eliteserien/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Instagram
              </Link>
            </SocialLinkWrap>
            <SocialLinkWrap>
              <SocialIconWrapper>
                <SocialIcon src={Youtube} alt="Youtube logo" />
              </SocialIconWrapper>
              <Link
                href="https://www.youtube.com/channel/UCKB1WCVnk1j3UeM-82MTScA"
                target="_blank"
                rel="noopener noreferrer"
              >
                Youtube
              </Link>
            </SocialLinkWrap>
            <SocialLinkWrap>
              <SocialIconWrapper>
                <SocialIcon src={Linkedin} alt="LinkedIn logo" />
              </SocialIconWrapper>
              <Link
                href="https://www.linkedin.com/company/norsk-toppfotball/"
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn
              </Link>
            </SocialLinkWrap>
            <SocialLinkWrap>
              <SocialIconWrapper>
                <SocialIcon src={TicTok} alt="TikTok logo" />
              </SocialIconWrapper>
              <Link
                href="https://www.tiktok.com/@eliteserien"
                target="_blank"
                rel="noopener noreferrer"
              >
                TikTok
              </Link>
            </SocialLinkWrap>
          </SocialLinksContainer>
          <ContentWrap>
            <div>
              <b>Foto</b>: NTB - <b>Redaktør</b>: Thomas Torjusen
            </div>
          </ContentWrap>
          <ContentWrap>
            <div>
              <Link href="https://www.eliteserien.no/vilkar-og-betingelser">
                {t("footer.termsAndConditions", "Terms and Conditions")}
              </Link>
            </div>
            <div>
              <Link href="https://www.eliteserien.no/personvern">
                {t("footer.privacyPolicy", "Privacy Policy")}
              </Link>
            </div>
          </ContentWrap>
        </Inner>
      </Wrapper>
    </StyledFooter>
  );
};

export default Footer;
